import React, { useState, useEffect } from 'react';
import { arrangeSnakeAlgorithm } from '../../Api/Api.jsx'
import * as THREE from 'three'; // Add this import
import { ArrangementParameters } from '../DeckSideBar/ArrangementParameters/ArrangementParameters.jsx'
import { check_collision_of_draggable_cargo_and_other_cargos } from '../DeckScene/ThreeFunctionsJS/dragObjectsFunctions.js';
import './DeckTable.css';

const DeckTable = ({ cargos, setCargos, cargoArea, arrangeParameters, onMessage }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [editingCell, setEditingCell] = useState({});
  const [modalEditing, setModalEditing] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  
  // Define which fields should be non-editable when inactive
  const nonEditableWhenActive = ['widthX', 'heightY', 'depthZ', 'x', 'y', 'z'];

    // Sync cargos prop with local data state
  useEffect(() => {
    if (cargos && cargos.length > 0) {
      // Convert cargos format to table data format
      const tableData = cargos.map(cargo => ({
        uuid: cargo.uuid,
        trackNumber: cargo.trackNumber,
        widthX: cargo.widthX,
        heightY: cargo.heightY,
        depthZ: cargo.depthZ,
        x: cargo.x,
        y: cargo.y,
        z: cargo.z,
        vehicle: cargo.vehicle,
        bol: cargo.bol,
        layer: cargo.layer,
        active: cargo.active,
        visible: cargo.visible, // default to true if not specified
        // set: cargo.set || false // Add set field, default to false
      }));
      
      setData(tableData);
      setFilteredData(tableData);
      console.log('Current data state in DeckTable:', tableData );           
    }
  }, [cargos]);

  // Global search
  useEffect(() => {
    console.log('Table data updated:', data);
    if (!searchTerm) {
      setFilteredData(data);
      return;
    }

    const filtered = data.filter(item =>
      Object.values(item).some(value =>
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredData(filtered);
  }, [searchTerm, data]);

  // Sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedData = [...filteredData].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredData(sortedData);
  };

  // Column filtering
  const handleFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    let filtered = data;
    Object.keys(newFilters).forEach(filterKey => {
      if (newFilters[filterKey]) {
        filtered = filtered.filter(item =>
          item[filterKey].toString().toLowerCase().includes(newFilters[filterKey].toLowerCase())
        );
      }
    });

    setFilteredData(filtered);
  };

  // Row selection
  const handleRowSelect = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(new Set(filteredData.map(item => item.uuid)));
    } else {
      setSelectedRows(new Set());
    }
  };

  // Delete selected rows (no scene update) - Recommended approach
  const handleDeleteSelected = () => {
    const newData = data.filter(item => !selectedRows.has(item.uuid));
    const deletedRows = data.filter(item => selectedRows.has(item.uuid));
    setData(newData);
    setFilteredData(newData);
    setSelectedRows(new Set()); // Clear selection
    setCargos(newData);
    onMessage(`Deleted: ${deletedRows.length} items`, 'info');
  };

  // Remove selected rows from scene
  const handleRemoveSelected = () => {
    const removedRows = data.filter(item => selectedRows.has(item.uuid) && item.active);
    
    const updatedData = data.map(item => 
      selectedRows.has(item.uuid) && item.active 
        ? { 
            ...item,
            active: false,
            visible: false,
            x: 0,
            y: 0,
            z: 0
          }
        : item
    );

    
    // Update states
    setData(updatedData);
    setFilteredData(updatedData);
    setSelectedRows(new Set());
    setCargos(updatedData);
    
    onMessage(`Removed from deck: ${removedRows.length} items`, 'info');
  };
  

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Track Number', 'Width X', 'Height Y', 'Depth Z', 'X', 'Y', 'Z', 'Vehicle', 'BoL', 'Layer', 'Active', 'Set', 'Visible'];
    const csvData = data.map(row => [
      row.trackNumber,
      row.widthX,
      row.heightY,
      row.depthZ,
      row.x,
      row.y,
      row.z,
      row.vehicle,
      row.bol,
      row.layer,
      row.active ? 'Yes' : 'No',
      // row.set ? 'Yes' : 'No', // Add set field to CSV
      row.visible ? 'Yes' : 'No'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '3d-objects-export.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    onMessage(`Exported ${data.length} rows`, 'info');
  };

  // Import from CSV with validation (no scene update)
  const handleImportCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvText = e.target.result;
      const lines = csvText.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      
      const newData = lines.slice(1).map((line, index) => {
        const values = line.split(',');
        
        // Parse numeric values with validation
        const widthX = parseInt(values[1]) || 0;
        const heightY = parseInt(values[2]) || 0;
        const depthZ = parseInt(values[3]) || 0;
        const x = parseInt(values[4]) || 0;
        const y = parseInt(values[5]) || 0;
        const z = parseInt(values[6]) || 0;
        const layer = parseInt(values[9]) || 1;

        // Validate numeric fields
        const numericFields = [
          { name: 'widthX', value: widthX },
          { name: 'heightY', value: heightY },
          { name: 'depthZ', value: depthZ },
          { name: 'x', value: x },
          { name: 'y', value: y },
          { name: 'z', value: z },
          { name: 'layer', value: layer }
        ];

        const invalidFields = numericFields.filter(field => 
          field.value <= 0 || isNaN(field.value)
        );

        if (invalidFields.length > 0) {
          console.warn(`Row ${index + 1} has invalid fields:`, invalidFields.map(f => f.name));
        }

        return {
          uuid: `${Date.now()}-${index}`,
          trackNumber: values[0] || 'IMPORTED',
          widthX: Math.max(0, widthX),
          heightY: Math.max(0, heightY),
          depthZ: Math.max(0, depthZ),
          x: Math.max(0, x),
          y: Math.max(0, y),
          z: Math.max(0, z),
          vehicle: values[7] || 'IMPORTED',
          bol: values[8] || 'IMPORTED',
          layer: Math.max(1, layer),
          active: values[10]?.toLowerCase() === 'yes',
          // set: values[11]?.toLowerCase() === 'yes', // Add set field import
          visible: values[12]?.toLowerCase() === 'yes'
        };
      }).filter(item => 
        // Filter out items with completely invalid data
        item.widthX > 0 && item.heightY > 0 && item.depthZ > 0
      );

      setData(prev => [...prev, ...newData]);
      setFilteredData(prev => [...prev, ...newData]);
      setCargos(prev => [...prev, ...newData]);
      event.target.value = ''; // Reset file input
      
      onMessage(`Imported ${newData.length} valid rows. ${lines.slice(1).length-newData.length} rows were skipped due to invalid data.`, 'info');
    };
    reader.readAsText(file);
  };

  // Updated handleArrangeSelected function - ONLY this updates the scene
  const handleArrangeSelected = () => {
    // Check if cargoArea is defined
    if (!cargoArea || cargoArea.length === 0) {
      onMessage('Please define cargo area first in the side panel', 'error');
      // alert('Please define cargo area first in the side panel');
      return;
    }

    // Arrange selected rows - only for inactive rows
    if (selectedRows.size === 0) {
      onMessage('Please select at least one row to arrange', 'error');
      // alert('Please select at least one row to arrange');
      return;
    }

    // Filter only active rows (already arranged)
    const activeRows = data.filter(item => item.active);

    // Filter only inactive rows from selected (to be arranged)
    const inactiveSelectedRows = data.filter(item => 
      selectedRows.has(item.uuid) && !item.active
    );

    if (inactiveSelectedRows.length === 0) {
      onMessage('Please select only INACTIVE rows to arrange. Active rows cannot be arranged.', 'error');
      // alert('Please select only INACTIVE rows to arrange. Active rows cannot be arranged.');
      return;
    }    

    const errorsN0 = validateNumeric0Fields(inactiveSelectedRows);
    if (errorsN0.length > 0) {
      onMessage('Validation Error:', '${errorsN0.join(', ')}');
      // alert(`Validation Error: ${errorsN0.join(', ')}`);
      return;
    }
    
    const errorsN = notZeroFields(inactiveSelectedRows);
    if (errorsN.length > 0) {
      onMessage('Validation Error:', '${errorsN.join(', ')}');
      // alert(`Validation Error: ${errorsN.join(', ')}`);
      return;
    }

    // Use snake algorithm for arrangement - ONLY this updates the scene via setCargos
    arrangeSnakeAlgorithm(
      activeRows, // already arranged
      inactiveSelectedRows, // not arranged yet
      cargoArea,
      arrangeParameters
    )
    .then(arrangedCuboids => {
      // Update the local data with arranged positions
      if (arrangedCuboids && arrangedCuboids.length > 0) {
        const updatedData = data.map(item => {
          const arrangedCargo = arrangedCuboids.find(cuboid => cuboid['#11_uuid'] === item.uuid);
          if (arrangedCargo) {
            return {
              ...item,
              x: parseInt(arrangedCargo['#15_position_x']),
              y: parseInt(arrangedCargo['#16_position_y']),
              z: parseInt(arrangedCargo['#17_position_z']),
              active: true,
              visible: true
            };
          }
          return item;
        });

        setData(updatedData);
        setFilteredData(updatedData);
        setSelectedRows(new Set()); // Clear selection
        setCargos(updatedData);
        onMessage(`Successfully arranged ${inactiveSelectedRows.length} cargos`, 'success');

      } else {
        onMessage('No cargos were arranged by the algorithm', 'info');
      }
    })
    .catch(error => {
      onMessage(`${error.message}`, 'error');
    });
  };  

  // Calculate inactive selected count
  const inactiveSelectedCount = React.useMemo(() => {
    return data.filter(item => selectedRows.has(item.uuid) && !item.active).length;
  }, [data, selectedRows]); 
  
  // Calculate active selected count
  const activeSelectedCount = React.useMemo(() => {
    return data.filter(item => selectedRows.has(item.uuid) && item.active).length;
  }, [data, selectedRows]); 

  // Cell editing with validation (WITH scene update for inactive cargos)
  const handleCellEdit = (id, key, value) => {
    const row = data.find(item => item.uuid === id);
    if (!row) return;

    // Check if it's a numeric field
    const numeric0Fields = ['widthX', 'heightY', 'depthZ', 'x', 'y', 'z', 'layer'];
    if (numeric0Fields.includes(key)) {
      const errorsN0 = validateNumeric0SingleField(key, value);
      if (errorsN0.length > 0) {
        onMessage(`Validation Error: ${errorsN0.join(', ')}`, 'error');
        return;
      }
    }
    
    const numericFields = ['widthX', 'heightY', 'depthZ', 'layer'];
    if (numericFields.includes(key)) {
      const errorsN = notZeroSingleField(key, value);
      if (errorsN.length > 0) {
        onMessage(`${errorsN.join(', ')}`, 'error');
        return;
      }
    }

    // Update the data
    const updatedData = data.map(item =>
      item.uuid === id 
        ? { 
            ...item, 
            [key]: convertToNumber(key, value) 
          } 
        : item
    );
    
    // CRITICAL: Always update the scene via setCargos for dimension changes
    setData(updatedData);
    setFilteredData(updatedData);
    setCargos(updatedData);
  };  

  // Modal editing with validation (no scene update)
  const handleModalEdit = (id, fieldUpdates) => {
    const validationErrors = [];
    
    // Validate numeric fields
    const numeric0Fields = ['widthX', 'heightY', 'depthZ', 'x', 'y', 'z', 'layer'];
    numeric0Fields.forEach(field => {
      if (fieldUpdates[field] !== undefined) {
        const errorsN0 = validateNumeric0SingleField(field, fieldUpdates[field]);
        validationErrors.push(...errorsN0);
      }
    });

    const numericFields = ['widthX', 'heightY', 'depthZ', 'layer'];
    numericFields.forEach(field => {
      if (fieldUpdates[field] !== undefined) {
        const errorsN = notZeroSingleField(field, fieldUpdates[field]);
        validationErrors.push(...errorsN);
      }
    });

    if (validationErrors.length > 0) {
      onMessage(`${validationErrors.join('\n')}`, 'error');
      // alert(`Validation Errors:\n${validationErrors.join('\n')}`);
      return; // Don't update if validation fails
    }

    // If validation passes, update the data (no scene update)
    const updatedData = data.map(item =>
      item.uuid === id ? { ...item, ...fieldUpdates } : item
    );
    setData(updatedData);
    setFilteredData(updatedData);
    setModalEditing(null);
  };

  // Add new row (no scene update)
  const handleAddRow = () => {
    const newRow = {
      uuid: Date.now().toString(),
      trackNumber: 'NEW',
      widthX: 0,
      heightY: 0,
      depthZ: 0,
      x: 0,
      y: 0,
      z: 0,
      vehicle: 'NEW',
      bol: 'NEW',
      layer: 1,
      active: false,
      // set: false, // Add set field with default false
      visible: true
    };
    
    const newData = [...data, newRow];
    setData(newData);
    setFilteredData(newData);
    setCargos(newData);
  };

  // Validation function for numeric0 fields
  const validateNumeric0Fields = (rows) => {
    const numericFields = ['widthX', 'heightY', 'depthZ', 'x', 'y', 'z', 'layer'];
    const errors = [];

    rows.forEach(rowData => {
      numericFields.forEach(field => {
        const value = rowData[field];
        // Check if value exists
        if (value === undefined || value === null || value === '') {
          errors.push(`${field} is required`);
          return;
        }

        // Convert to number
        const numValue = Number(value);
        
        // Check if it's a valid number
        if (isNaN(numValue)) {
          errors.push(`${field} must be a valid number`);
          return;
        }

        // Check if positive
        if (numValue < 0) {
          errors.push(`${field} must be positive`);
          return;
        }

        // Check for leading zeros in the original string
        if (value.toString().startsWith('0') && value.toString().length > 1) {
          errors.push(`${field} cannot have leading zeros`);
          return;
        }

        // Check if it's an integer (if needed)
        if (!Number.isInteger(numValue)) {
          errors.push(`${field} must be an integer`);
          return;
        }
      });
    });
    return errors;
  };

  // Validation function for not Zero fields
  const notZeroFields = (rows) => {
    const notZeroFields = ['widthX', 'heightY', 'depthZ', 'layer'];
    const errors = [];

    rows.forEach(rowData => {
      notZeroFields.forEach(field => {
        const value = rowData[field];

        // Convert to number
        const numValue = Number(value);
        
        // Check if zero
        if (numValue === 0) {
          errors.push(`${field} must be > 0`);
          return;
        }
      });
    });
    return errors;
  };

  // Validate value single field - Positive Numeric with 0
  const validateNumeric0SingleField = (field, value) => {
    const errors = [];
    
    // Check if value exists
    if (value === undefined || value === null || value === '') {
      errors.push(`${field} is required`);
      return errors;
    }

    // Convert to number
    const numValue = Number(value);
    
    // Check if it's a valid number
    if (isNaN(numValue)) {
      errors.push(`${field} must be a valid number`);
      return errors;
    }

    // Check if positive
    if (numValue < 0) {
      errors.push(`${field} must be positive`);
      return errors;
    }

    // Check for leading zeros
    if (value.toString().startsWith('0') && value.toString().length > 1) {
      errors.push(`${field} cannot have leading zeros`);
      return errors;
    }

    // Check if it's an integer
    if (!Number.isInteger(numValue)) {
      errors.push(`${field} must be an integer`);
      return errors;
    }

    return errors;
  };

  // Validate value single field - Positive Numeric without zero
  const notZeroSingleField = (field, value) => {
    const errors = [];

    // Convert to number
    const numValue = Number(value);
    
    // Check if zero
    if (numValue === 0) {
      errors.push(`${field} must be > 0`);
      return errors;
    }
    return errors;
  };

  // Delete row (no scene update)
  const handleDeleteRow = (id) => {
    const newData = data.filter(item => item.uuid !== id);
    setData(newData);
    setFilteredData(newData);
    const newSelected = new Set(selectedRows);
    newSelected.delete(id);
    setSelectedRows(newSelected);
    setCargos(newData);
    onMessage(`Single row deleted`, 'info');
  };

  // Toggle set - check collision
  // Toggle set - check collision
  const toggleSet = (id) => {
    const rowData = data.find(item => item.uuid === id);
    
    if (!rowData) return;

    // Check if cargoArea is defined
    if (!cargoArea || cargoArea.length === 0) {
      onMessage('Please define cargo area first in the side panel', 'info');
      return;
    }

    const errorsN0 = validateNumeric0Fields([rowData]);
    if (errorsN0.length > 0) {
      onMessage(`${errorsN0.join(', ')}`, 'error');
      // alert(`Validation Error: ${errorsN0.join(', ')}`);
      return;
    }
    
    const errorsN = notZeroFields([rowData]);
    if (errorsN.length > 0) {
      onMessage(`${errorsN.join(', ')}`, 'error');
      // alert(`Validation Error: ${errorsN.join(', ')}`);
      return;
    }

    // Filter only active rows from data
    const activeRows = data.filter(item => item.active && item.uuid !== id);


    if (!checkCargoCollision(rowData,activeRows,cargoArea)) {
      // Update the specific row in the data array
      const updatedData = data.map(item => 
        item.uuid === rowData.uuid 
          ? { 
              ...rowData, 
              active: true, 
              visible: true 
            }
          : item
      );

      setData(updatedData);
      setFilteredData(updatedData);
      setSelectedRows(new Set());
      setCargos(updatedData);
      onMessage(`Item successfully placed`, 'info');
    } else {
      onMessage('Collision detected! Cannot place item', 'error');
    }

  };  

  // Change dimensions - swap width and depth
  const changeDimensions = (id) => {
    const rowData = data.find(item => item.uuid === id); //&& !item.active);
    
    if (!rowData) return;

    // Check if cargoArea is defined
    if (!cargoArea || cargoArea.length === 0) {
      onMessage('Please define cargo area first in the side panel', 'info');
      return;
    }

    // Swap widthX and depthZ and prepare for collision check
    const swappedData = data.map(item =>
      item.uuid === id
        ? {
            ...item,
            widthX: item.depthZ,
            depthZ: item.widthX,
          }
        : item
    );

    setData(swappedData);
    setFilteredData(swappedData);
    setCargos(swappedData);
    onMessage(`Width<->Depth successfully changed`, 'info');
    
    // const swappedRow = swappedData.find(item => item.uuid === id);

    // // Validate the swapped row
    // const errorsN0 = validateNumeric0Fields([swappedRow]);
    // if (errorsN0.length > 0) {
    //   alert(`Validation Error: ${errorsN0.join(', ')}`);
    //   return;
    // }
    
    // const errorsN = notZeroFields([swappedRow]);
    // if (errorsN.length > 0) {
    //   alert(`Validation Error: ${errorsN.join(', ')}`);
    //   return;
    // }

    // // Check collision with the swapped dimensions
    // const activeRows = swappedData.filter(item => item.active && item.uuid !== id);
    // const collisionResult = checkCargoCollision(swappedRow, activeRows, cargoArea);

    // if (!collisionResult) {
    //   // If no collision, activate immediately
    //   const finalData = swappedData.map(item =>
    //     item.uuid === id
    //       ? {
    //           ...item,
    //           active: true,
    //           visible: true
    //         }
    //       : item
    //   );
      
    //   setData(finalData);
    //   setFilteredData(finalData);
    //   setSelectedRows(new Set());
    //   setCargos(finalData);
    //   console.log('✅ Dimensions swapped and cargo activated');
    // } else {
      // // If collision, just swap dimensions but keep inactive
      // // BUT ensure it's visible so the scene updates
      // const finalInactiveData = swappedData.map(item =>
      //   item.uuid === id
      //     ? {
      //         ...item,
      //         active: false,
      //         visible: true, // Keep visible even when inactive
      //         x: rowData.x,  // Keep original position
      //         y: rowData.y,
      //         z: rowData.z
      //       }
      //     : item
      // );
      
      // setData(finalInactiveData);
      // setFilteredData(finalInactiveData);
      // setCargos(finalInactiveData);
      // console.log('🔄 Dimensions swapped but cargo kept inactive due to collision');
      // onMessage('Collision detected! Cargo dimensions swapped but kept inactive', 'info');
    // }
  };

  // Updated toggleActive function - ONLY arrangement updates scene
  const toggleActive = (id, arrangeOption = 0) => {
    const rowData = data.find(item => item.uuid === id);
    
    if (!rowData) return;

    // Create a copy of arrangeParameters with the arrangeOption
    const arrangementParams = {
      ...arrangeParameters,
      arrangementOptions: arrangeOption
    };

    // If activating (arranging), use snake algorithm (updates scene)
    if (!rowData.active) {
      // Check if cargoArea is defined
      if (!cargoArea || cargoArea.length === 0) {
        onMessage('Please define cargo area first in the side panel', 'info');
        return;
      }

      const errorsN0 = validateNumeric0Fields([rowData]);
      if (errorsN0.length > 0) {
        onMessage(`${errorsN0.join(', ')}`, 'error');
        return;
      }
      
      const errorsN = notZeroFields([rowData]);
      if (errorsN.length > 0) {
        onMessage(`${errorsN.join(', ')}`, 'error');
        return;
      }

      // Filter only active rows from data
      const activeRows = data.filter(item => item.active);

      // Use snake algorithm for single item arrangement (updates scene via setCargos)
      arrangeSnakeAlgorithm(
        activeRows, // arranged yet
        [rowData],  // not arranged      
        cargoArea,
        arrangementParams
      )
      .then(arrangedCuboids => {
        if (arrangedCuboids && arrangedCuboids.length > 0) {
          const updatedData = data.map(item => {
            const arrangedCargo = arrangedCuboids.find(cuboid => cuboid['#11_uuid'] === item.uuid);
            if (arrangedCargo) {
              return {
                ...item,
                x: parseInt(arrangedCargo['#15_position_x']),
                y: parseInt(arrangedCargo['#16_position_y']),
                z: parseInt(arrangedCargo['#17_position_z']),
                active: true,
                visible: true // PRESERVE VISIBILITY STATE
              };
            }
            return item;
          });
          setData(updatedData);
          setFilteredData(updatedData);
          setCargos(updatedData);
          onMessage('Successfully arranged 1 cargo', 'info');
        } else {
          onMessage('No cargo was arranged', 'info');
        }
      })
      .catch(error => {
        onMessage('Error arranging cargos. Please try again.', 'info');
      });
    } else {
      // If deactivating (getting off), just update local state and reset position
      const updatedData = data.map(item =>
        item.uuid === id ? { 
          ...item, 
          active: false,
          x: 0,  // Reset position to origin
          y: 0,
          z: 0
        } : item
      );
      setData(updatedData);
      setFilteredData(updatedData);
      setCargos(updatedData); // This updates the scene to remove the cargo
      onMessage(`Item removed`, 'info');

    }
  };

  // Toggle visible status (no scene update)
  const toggleVisible = (id) => {
    const updatedData = data.map(item =>
      item.uuid === id ? { 
        ...item, 
        visible: !item.visible 



      } : item
    );

    setData(updatedData);
    setFilteredData(updatedData);
    setSelectedRows(new Set());
    setCargos(updatedData);
  };

  // Helper function to check all cargo intersections
  // Helper function to check all cargo intersections
  const checkCargoCollision = (newCargo, existingCargos, cargoArea) => {
    // Create a THREE.Box3 for the new cargo
    const newCargoBox = createBoundingBox(newCargo);
    
    // Check against cargo area boundaries
    if (!isWithinCargoArea(newCargoBox, cargoArea)) {
      return true; // Collision with boundary
    }

    // Check against other cargos
    if (existingCargos.length > 0) {  
      for (const existing of existingCargos) {
        // Create a THREE.Box3 for the existing cargo
        const existingBox = createBoundingBox(existing);
        
        // Use intersectsBox to check collision
        if (checkBoundingBoxCollision(newCargoBox,existingBox)) {
          return true; // Collision with existing cargo
        }
      }
    }
    
    return false; // No collisions
  };  

  // Helper function to create a THREE.Box3 from cargo data
  const createBoundingBox = (cargo) => {
    // Parse numeric values
    const x = parseFloat(cargo.x) || 0;
    const y = parseFloat(cargo.y) || 0;
    const z = parseFloat(cargo.z) || 0;
    const width = parseFloat(cargo.widthX) || 0;
    const height = parseFloat(cargo.heightY) || 0;
    const depth = parseFloat(cargo.depthZ) || 0;
    
    // Create min and max vectors for the bounding box
    const min = new THREE.Vector3(
      x,
      y,
      z
    );
    
    const max = new THREE.Vector3(
      x + width,
      y + height,
      z + depth
    );
    
    return new THREE.Box3(min, max);
  };


  const isWithinCargoArea = (cargoBox, cargoArea) => {
    if (!cargoArea || cargoArea.length === 0) return false;
    
    // Assuming cargoArea[0] contains the boundary dimensions
    const areaWidth = parseFloat(cargoArea[0].widthX) || 0;
    const areaHeight = parseFloat(cargoArea[0].heightY) || 0;
    const areaDepth = parseFloat(cargoArea[0].depthZ) || 0;
    
    // Create bounding box for the cargo area (starting at 0,0,0)
    const areaMin = new THREE.Vector3(0, 0, 0);
    const areaMax = new THREE.Vector3(areaWidth, areaHeight, areaDepth);
    const areaBox = new THREE.Box3(areaMin, areaMax);
    
    // Check if cargo box is completely within area box
    return areaBox.containsBox(cargoBox);
  };  

  const checkBoundingBoxCollision = (box1, box2) => {
    // Optionally add some padding/margin to avoid exact touching
    const padding = 0.01; // 1cm padding
    
    // Create padded boxes
    const paddedBox1 = box1.clone();
    paddedBox1.expandByScalar(-padding); // Shrink box1 slightly
    
    const paddedBox2 = box2.clone();
    paddedBox2.expandByScalar(-padding); // Shrink box2 slightly
    
    // Check intersection with padding
    return paddedBox1.intersectsBox(paddedBox2);
  };  

  // Simple conversion function
  const convertToNumber = (key, value) => {
    const numericFields = ['widthX', 'heightY', 'depthZ', 'x', 'y', 'z', 'layer'];

    if (!numericFields.includes(key)) return value;
    
    const num = Number(value);
    // Return 0 for invalid numbers to prevent string positions
    return isNaN(num) ? 0 : num;
  };


  // Column definitions
  const columns = [
    { key: 'trackNumber', label: 'Track Number', width: '150px' },
    { key: 'widthX', label: 'Width X', width: '100px' },
    { key: 'heightY', label: 'Height Y', width: '100px' },
    { key: 'depthZ', label: 'Depth Z', width: '100px' },
    { key: 'x', label: 'X', width: '80px' },
    { key: 'y', label: 'Y', width: '80px' },
    { key: 'z', label: 'Z', width: '80px' },
    { key: 'vehicle', label: 'Vehicle', width: '120px' },
    { key: 'bol', label: 'BoL', width: '120px' },
    { key: 'layer', label: 'Layer', width: '30px' }
  ];

  return (
    <div className="deck-table-container">
      {/* Modern Controls Bar */}
      <div className="modern-controls-bar">
        <div className="controls-group">
          <button 
            onClick={handleAddRow} 
            className="btn btn-modern btn-primary"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M12 5v14M5 12h14" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Add Track
          </button>
          
          <button 
            onClick={handleDeleteSelected} 
            className="btn btn-modern btn-danger"
            disabled={selectedRows.size === 0}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeWidth="2"/>
            </svg>
            Delete ({selectedRows.size})
          </button>

          <button 
            onClick={handleRemoveSelected} 
            className="btn btn-modern btn-inform"
            disabled={activeSelectedCount===0}
          >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2"/>
            <path d="M8 12H16" strokeWidth="2" strokeLinecap="round"/>
          </svg>
            Remove ({activeSelectedCount})
          </button>

          <button onClick={handleExportCSV} className="btn btn-modern btn-success">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Export CSV
          </button>

          <label className="btn btn-modern btn-info file-input-label">
            <input
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              className="file-input"
            />
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Import CSV
          </label>

          <button 
            onClick={handleArrangeSelected} 
            className="btn btn-modern btn-execute"
            disabled={inactiveSelectedCount === 0}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M10 4H4a2 2 0 00-2 2v4h8V4zM10 14H4a2 2 0 00-2 2v4h8v-6zM20 4h-6a2 2 0 00-2 2v4h8V4zM20 14h-6a2 2 0 00-2 2v4h8v-6z" strokeWidth="2"/>
            </svg>
            Arrange ({inactiveSelectedCount})
          </button>
        </div>

        <div className="search-container">
          <div className="search-box-modern">
            <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search any..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input-modern"
            />
          </div>
          
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th className="checkbox-column">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedRows.size === filteredData.length && filteredData.length > 0}
                />
              </th>
              <th className="status-column">Status</th>
              {columns.map(column => (
                <th key={column.key} style={{ width: column.width }}>
                  <div className="header-content">
                    <div 
                      className="column-label sortable"
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                      {sortConfig.key === column.key && (
                        <span className="sort-indicator">
                          {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </div>
                    <input
                      type="text"
                      placeholder="Filter..."
                      value={filters[column.key] || ''}
                      onChange={(e) => handleFilter(column.key, e.target.value)}
                      className="filter-input"
                    />
                  </div>
                </th>
              ))}
              <th className="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map(row => (
              <tr 
                key={row.uuid} 
                className={`${!row.active ? 'inactive' : ''} ${!row.visible ? 'hidden' : ''} ${selectedRows.has(row.uuid) ? 'selected' : ''}`}
              >
                <td className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.has(row.uuid)}
                    onChange={() => handleRowSelect(row.uuid)}
                  />
                </td>

                <td className="status-column">
                  <div className="status-buttons">
                    <button
                      onClick={() => toggleActive(row.uuid)}
                      className={`btn-icon ${row.active ? 'active' : 'inactive'}`}
                      title={row.active ? 'Remove' : 'Auto arrange'}
                    >
                      {row.active ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M10 4H4a2 2 0 00-2 2v4h8V4zM10 14H4a2 2 0 00-2 2v4h8v-6zM20 4h-6a2 2 0 00-2 2v4h8V4zM20 14h-6a2 2 0 00-2 2v4h8v-6z" strokeWidth="2"/>
                        </svg>
                      )}
                    </button>
                    
                    {/* Show Set button only when inactive */}
                    {!row.active && (
                      <button
                        onClick={() => toggleSet(row.uuid)}
                        className={`btn-icon ${row.active ? 'active' : 'inactive'}`}
                        title={row.active ? 'Set coordinates manually' : 'Set coordinates manually'}
                      >
                        {row.active ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <circle cx="12" cy="12" r="3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>                        
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M12 4v4m0 4v4m0 4v4M4 12h4m4 0h4m4 0h4" stroke-width="2" stroke-linecap="round"/>
                            <circle cx="12" cy="12" r="3" stroke-width="2"/>
                          </svg>
                        )}
                      </button>
                    )}
                    
                    <button
                      onClick={() => toggleVisible(row.uuid)}
                      className={`btn-icon ${row.visible ? 'visible' : 'hidden'}`}
                      title={row.visible ? 'Hide' : 'Show'}
                    >
                      {row.visible ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M1 1l22 22" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>

                    {/* <button
                      onClick={() => changeDimensions(row.uuid)}
                      className="btn-icon"
                      title="Swap width and depth dimensions"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M8 3L4 7l4 4M4 7h16M16 21l4-4-4-4M20 17H4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button> */}
                    
                    {/* Change Dimensions button - ONLY show when inactive */}
                    {!row.active && (
                      <button
                        onClick={() => changeDimensions(row.uuid)}
                        className="btn-icon"
                        title="Swap width and depth dimensions"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M8 3L4 7l4 4M4 7h16M16 21l4-4-4-4M20 17H4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )} 

                  </div>
                </td>

                {columns.map(column => (
                  <td key={column.key} style={{ width: column.width }}>
                    {/* Check if field should be non-editable when inactive */}
                    {row.active && nonEditableWhenActive.includes(column.key) ? (
                      // Non-editable display for inactive rows
                      <span 
                        className="non-editable-cell"
                        title={String(row[column.key])}
                      >
                        {String(row[column.key])}
                      </span>
                    ) : editingCell?.id === row.uuid && editingCell?.key === column.key ? (
                      // Editable input
                      <input
                        type="text"
                        value={row[column.key]}
                        onChange={(e) => handleCellEdit(row.uuid, column.key, e.target.value)}
                        onBlur={() => setEditingCell(null)}
                        onKeyPress={(e) => e.key === 'Enter' && setEditingCell(null)}
                        className="cell-input"
                        autoFocus
                      />
                    ) : (
                      // Normal editable cell
                      <span
                        onClick={() => setEditingCell({ id: row.uuid, key: column.key })}
                        className="editable-cell"
                        title={String(row[column.key])}
                      >
                        {String(row[column.key])}
                      </span>
                    )}
                  </td>
                ))}
                
                <td className="actions-column">
                  <div className="action-buttons">

                    {/* <button
                      onClick={() => setModalEditing(row)}
                      className="btn-icon"
                      title="Edit in modal"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeWidth="2"/>
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" strokeWidth="2"/>
                      </svg>
                    </button> */}

                    <button
                      onClick={() => handleDeleteRow(row.uuid)}
                      className="btn-icon btn-danger"
                      title="Delete"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeWidth="2"/>
                      </svg>
                    </button>                    
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredData.length === 0 && (
          <div className="empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeWidth="2"/>
            </svg>
            <h3>No tracks found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Modal for editing - also update to disable fields when inactive */}
      {modalEditing && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit Track</h3>
            <div className="modal-content">
              {columns.map(column => (
                <div key={column.key} className="form-group">
                  <label>{column.label}</label>
                  <input
                    type="text"
                    value={modalEditing[column.key]}
                    onChange={(e) => setModalEditing({
                      ...modalEditing,
                      [column.key]: e.target.value
                    })}
                    className={`form-input ${
                      modalEditing.active && nonEditableWhenActive.includes(column.key) 
                        ? 'disabled-input' 
                        : ''
                    }`}
                    disabled={modalEditing.active && nonEditableWhenActive.includes(column.key)}
                  />
                  {modalEditing.active && nonEditableWhenActive.includes(column.key) && (
                    <div className="disabled-hint">
                      This field cannot be edited while the track is inactive
                    </div>
                  )}
                </div>
              ))}
              <div className="form-group status-controls">
                <label>Status</label>
                <div className="toggle-buttons">
                  <button
                    onClick={() => setModalEditing({...modalEditing, active: !modalEditing.active})}
                    className={`toggle-btn ${modalEditing.active ? 'active' : ''}`}
                  >
                    {modalEditing.active ? 'Active' : 'Inactive'}
                  </button>
                  
                  {/* Show Set toggle only when inactive */}
                  {!modalEditing.active && (
                    <button
                      onClick={() => setModalEditing({...modalEditing, set: !modalEditing.set})}
                      className={`toggle-btn ${modalEditing.set ? 'active' : ''}`}
                    >
                      {modalEditing.set ? 'Set' : 'Not Set'}
                    </button>
                  )}
                  
                  <button
                    onClick={() => setModalEditing({...modalEditing, visible: !modalEditing.visible})}
                    className={`toggle-btn ${modalEditing.visible ? 'active' : ''}`}
                  >
                    {modalEditing.visible ? 'Visible' : 'Hidden'}
                  </button>
                </div>
              </div>

            </div>
            <div className="modal-actions">
              <button onClick={() => setModalEditing(null)} className="btn btn-secondary">
                Cancel
              </button>
              <button 
                onClick={() => handleModalEdit(modalEditing.uuid, modalEditing)}
                className="btn btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )} 
    </div>
  );
};

export default DeckTable;